import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { PlugZap, Building2, ScanSearch, Layers, LogOut, Globe, LayoutDashboard, CheckSquare, Hammer, Inbox, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtCid } from "@/lib/api";
import { ReauthBanner } from "@/components/ReauthBanner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, id: "approvals" },
  { to: "/leads", label: "Lead Inbox", icon: Inbox, id: "leads" },
  { to: "/builder", label: "Campaign Builder", icon: Hammer, id: "builder" },
  { to: "/changes", label: "Change Log", icon: History, id: "changes" },
  { to: "/settings", label: "Connect & Data", icon: PlugZap, id: "settings" },
  { to: "/profile", label: "Business Profile", icon: Building2, id: "profile" },
  { to: "/audit", label: "Account Audit", icon: ScanSearch, id: "audit" },
  { to: "/plan", label: "Campaign Plan", icon: Layers, id: "plan" },
  { to: "/landing-pages", label: "Landing Pages", icon: Globe, id: "landing-pages" },
];

const Beacon = ({ status }) => {
  const connected = status?.connected;
  const color = connected ? "bg-emerald-400" : status?.state === "not_authorized" ? "bg-amber-400" : "bg-red-400";
  return (
    <div data-testid="header-connection-beacon" className="flex items-center gap-3 rounded-md border border-[#1e293b] bg-[#0a101d] px-3 py-1.5">
      <span className="relative flex h-2.5 w-2.5">
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${color}`} />
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${color}`} />
      </span>
      <span className="font-mono text-[11px] uppercase tracking-wider text-slate-300">
        {status ? (connected ? `Google Ads · ${status.selected_account ? fmtCid(status.selected_account.customer_id) : "no account picked"}` : status.label) : "Checking…"}
      </span>
    </div>
  );
};

const Badge = ({ b, id }) => (b?.count > 0 ? (
  <span data-testid={`nav-badge-${id}`} title={b.tooltip} aria-label={b.tooltip}
    className={`ml-auto min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center font-mono text-[10px] font-bold leading-none ${b.tone === "red" ? "bg-red-500 text-white" : "bg-amber-400 text-slate-950"}`}>{b.count}</span>
) : null);

const useBadges = () => {
  const [badges, setBadges] = useState({});
  useEffect(() => {
    const load = () => api.get("/nav/badges").then((r) => setBadges(r.data)).catch(() => {});
    const tick = () => { if (document.visibilityState === "visible") load(); };
    load();
    const t = setInterval(tick, 60000);
    window.addEventListener("nav-badges-refresh", load);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(t); window.removeEventListener("nav-badges-refresh", load); document.removeEventListener("visibilitychange", tick); };
  }, []);
  useEffect(() => { const n = badges.title_count || 0; document.title = n > 0 ? `(${n}) Ads Monster` : "Ads Monster"; }, [badges.title_count]);
  return badges;
};

const NavItems = ({ onClick, badges }) =>
  NAV.map(({ to, label, icon: Icon, id }) => (
    <NavLink
      key={to}
      to={to}
      onClick={onClick}
      title={badges?.[id]?.count > 0 ? `${label}: ${badges[id].tooltip}` : label}
      data-testid={`nav-${id}-link`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 ${
          isActive ? "bg-emerald-500/10 text-emerald-300 shadow-[inset_2px_0_0_#10b981]" : "text-slate-400 hover:bg-[#162036] hover:text-slate-100"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="whitespace-nowrap">{label}</span>
      <Badge b={badges?.[id]} id={id} />
    </NavLink>
  ));

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(null);
  const badges = useBadges();

  const refreshStatus = useCallback(async () => {
    try {
      const [{ data }, { data: ap }] = await Promise.all([api.get("/google-ads/status"), api.get("/settings/autopilot")]);
      setStatus(data);
      setMode(ap);
    } catch {
      /* handled by interceptor */
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [location.pathname, refreshStatus]);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#070a11]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-[#1e293b] bg-[#0a0f1a] lg:flex">
        <div className="px-6 pb-8 pt-7">
          <div className="font-display text-2xl font-extrabold uppercase leading-none tracking-tight text-slate-50">
            Ads<span className="text-emerald-400">Monster</span>
          </div>
          <div className="cc-eyebrow mt-2">The Rehab Lab · SG</div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-3">
          <NavItems badges={badges} />
        </nav>
        <div className="border-t border-[#1e293b] p-4">
          <div className="cc-eyebrow">Signed in</div>
          <div data-testid="sidebar-user-email" className="mt-1 truncate text-sm text-slate-300">{user?.email}</div>
          <button data-testid="logout-button" onClick={doLogout} className="cc-btn cc-btn-ghost mt-3 w-full">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col lg:ml-64">
        <header className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-[#1e293b] bg-[#070a11]/90 px-6 py-3 backdrop-blur-md lg:px-10">
          <div className="flex items-center gap-1 overflow-x-auto lg:hidden"><NavItems badges={badges} /></div>
          <div className="cc-eyebrow hidden lg:block" data-testid="mode-banner">{mode ? `${mode.mode === "autopilot" ? "Autopilot · low/medium risk runs automatically" : "Approve mode · nothing changes without your approval"} · Live execution ${mode.execution_enabled ? "ON" : "OFF"}` : ""}</div>
          <Beacon status={status} />
        </header>
        <main className="w-full max-w-[1400px] px-6 py-10 lg:px-10 lg:py-12">
          {["/dashboard", "/approvals", "/settings"].includes(location.pathname) && <ReauthBanner status={status} />}
          <Outlet context={{ status, refreshStatus }} />
        </main>
      </div>
    </div>
  );
}
